import Menu from "./menu";
import { trackSearchInput } from "./analytics";
import { bindFiltersToggle, clearFilters, recordRecentlyViewed, renderRecentlyViewed, seedLocaleFilter } from "./search-shared";

// The header search, in two presentations of the same panel:
//
// - ≥700px: an overlay (position: absolute under the header, with a
//   backdrop and a frozen body), opened by focusing the always-editable
//   header input.
// - <700px: a same-page "takeover" (openTakeover()). .search-mobile-trigger
//   (search.scss) covers the header input; tapping it hides the page
//   content via body.search-takeover, leaves the panel in normal document
//   flow (no position: fixed, so none of the iOS keyboard/visualViewport
//   bugs that used to put it off-screen), and focuses the real input inside
//   the tap's own click handler — iOS only opens the keyboard for a focus()
//   made during a user gesture, which is why the earlier "navigate to
//   /search/ and autofocus on load" approach left the field focused but
//   with no keyboard, needing a second tap. The URL still changes to
//   /search/ (pushState), so Back closes the takeover and a reload lands on
//   the real /search/ page (search-page.ts).
class Search {
    private _header: HTMLElement | null = null;
    private _headerTop: HTMLElement | null = null;
    private _searchWrap: HTMLElement | null = null;
    private _panel: HTMLElement | null = null;
    private _backdrop: HTMLElement | null = null;
    private _recentList: HTMLElement | null = null;
    private _clearFiltersBtn: HTMLButtonElement | null = null;
    private _escHint: HTMLButtonElement | null = null;
    private _input: HTMLInputElement | null = null;

    private _mobileTrigger: HTMLAnchorElement | null = null;
    private _cancelBtn: HTMLButtonElement | null = null;

    private _isOpen: boolean;
    private _hasSeededLocale: boolean;
    private _frozenScrollY: number;
    private _isTakeover = false;
    private _pushedHistory = false;

    init() {
        this._header = document.querySelector<HTMLElement>("#site-header");
        if (!this._header) {
            return;
        }

        this._headerTop = this._header.querySelector<HTMLElement>(".header-top");
        this._searchWrap = this._header.querySelector<HTMLElement>("#search-wrap");
        this._panel = this._header.querySelector<HTMLElement>("#search-panel");
        this._backdrop = document.querySelector<HTMLElement>("#search-backdrop");
        this._recentList = document.querySelector<HTMLElement>("#search-recent-list");
        this._clearFiltersBtn = document.querySelector<HTMLButtonElement>("#search-clear-filters");
        this._escHint = document.querySelector<HTMLButtonElement>("#search-esc-hint");
        this._mobileTrigger = document.querySelector<HTMLAnchorElement>(".search-mobile-trigger");
        this._cancelBtn = document.querySelector<HTMLButtonElement>("#search-cancel");

        // #search-wrap/#search-panel don't exist at all on the dedicated
        // /search/ page (body-header.html omits them there) — nothing here
        // to bind.
        if (!this._headerTop || !this._panel || !this._backdrop) {
            return;
        }
        const panel = this._panel;

        this._isOpen = false;
        this._hasSeededLocale = false;

        recordRecentlyViewed();
        renderRecentlyViewed(this._recentList);

        // .header-top's own height changes across breakpoints (mobile wraps
        // the search field to its own row below the logo) and can change at
        // runtime (font loading, window resize) — ResizeObserver keeps the
        // panel's offset correct rather than baking in one breakpoint's
        // measurement.
        new ResizeObserver(() => this.syncPanelOffset()).observe(this._headerTop);
        this.syncPanelOffset();

        this.bindInput();

        document.addEventListener("keydown", (e) => this.handleKeydown(e));
        this._backdrop.addEventListener("click", () => this.close());
        this._escHint?.addEventListener("click", () => this.close());
        this._clearFiltersBtn?.addEventListener("click", () => clearFilters(panel, !!panel.dataset.seedLocale));
        bindFiltersToggle(panel);

        // Keyboard: tabbing out of the field and panel closes the overlay,
        // the keyboard equivalent of handleOutsideClick() below. Without
        // this, tabbing past the field left the overlay open (and the body
        // frozen) while focus moved on to the facet-nav behind it. Only
        // when focus actually lands somewhere (relatedTarget set) — a mouse
        // press on a non-focusable part of the panel blurs the input with a
        // null relatedTarget and must not close it.
        [this._searchWrap, panel].forEach((el) =>
            el?.addEventListener("focusout", (e: FocusEvent) => {
                const next = e.relatedTarget as Node | null;
                if (!this._isOpen || this._isTakeover || !next) {
                    return;
                }
                if (!this._searchWrap?.contains(next) && !panel.contains(next)) {
                    this.close();
                }
            }),
        );

        this._mobileTrigger?.addEventListener("click", (e) => this.openTakeover(e));
        this._cancelBtn?.addEventListener("click", () => this.close());
        window.addEventListener("popstate", () => {
            // Back from the takeover's pushed /search/ entry.
            if (this._isTakeover) {
                this._pushedHistory = false;
                this.close();
            }
        });

        // Any other header trigger (locale switcher, a mega-menu tab) closes
        // search first — the mirror image of Menu's own mutual-exclusion
        // pass over its panels, so search doesn't end
        // up trapped open underneath one of them.
        document.querySelectorAll(".locale-switch-trigger, .facet-tab.has-mega").forEach((trigger) => trigger.addEventListener("click", () => this.close()));
    }

    // .search-panel is positioned absolutely within #site-header (a sibling
    // of .header-top, not nested inside the input's own flex column — see
    // body-header.html) so it can be wide/centered independent of the
    // input's own width. Deliberately biasing 1px into overlap (floor, then
    // one more px) rather than rounding guarantees the panel's top edge
    // always sits a hair under .header-top's own box regardless of which
    // way the true fractional height would round — search.scss gives
    // .header-top a higher z-index than the panel/backdrop specifically so
    // that overlap is invisible rather than needing pixel-perfect alignment.
    private syncPanelOffset() {
        if (!this._headerTop || !this._panel) {
            return;
        }

        const headerHeight = this._headerTop.getBoundingClientRect().height;
        const top = Math.floor(headerHeight) - 1;
        this._panel.style.top = `${top}px`;
    }

    // <pagefind-input> renders its own native <input> into the light DOM
    // once the pagefind-component-ui.js module upgrades the element — which
    // in practice has already happened by the time this runs (that script
    // is declared in <head> and executes, in document order, before our own
    // deferred bundle script). The MutationObserver fallback only matters if
    // that ordering ever changes.
    private bindInput() {
        if (!this._header) {
            return;
        }

        // pagefind-input lives in #search-wrap, inside .header-top — a
        // sibling of #search-panel, not a descendant of it.
        const tryBind = (): boolean => {
            const input = this._header?.querySelector<HTMLInputElement>("pagefind-input input");
            if (!input) {
                return false;
            }

            this._input = input;
            input.addEventListener("focus", () => this.open());
            input.addEventListener("input", () => this.syncQueryState());
            trackSearchInput(input, "header");
            this.syncQueryState();
            return true;
        };

        if (tryBind()) {
            return;
        }

        const observer = new MutationObserver(() => {
            if (tryBind()) {
                observer.disconnect();
            }
        });
        observer.observe(this._header, { childList: true, subtree: true });
    }

    // The panel must fully hide the summary/filter/results columns on an
    // empty query rather than show them with a stale count or nothing in
    // them — data-query drives that in search.scss.
    private syncQueryState() {
        if (!this._input || !this._panel) {
            return;
        }

        const hasQuery = !!this._input.value.trim();
        this._panel.dataset.query = hasQuery ? "active" : "empty";
        this._searchWrap?.classList.toggle("has-query", hasQuery);
    }

    // Mobile only (.search-mobile-trigger is display: none ≥700px). Everything
    // up to focus() must stay synchronous — an await or timer before it
    // would take the focus() out of the tap's user-gesture window and iOS
    // would again focus the field without opening the keyboard.
    private openTakeover(e: MouseEvent) {
        const input = this._input;
        if (!input || !this._mobileTrigger) {
            // Pagefind hasn't rendered the real input yet — let the trigger
            // fall through to its plain /search/ link.
            return;
        }

        e.preventDefault();

        this._isTakeover = true;
        this._frozenScrollY = window.scrollY;
        document.body.classList.add("search-takeover");
        window.scrollTo(0, 0);
        input.focus(); // fires open() via the input's focus listener

        if (!this._pushedHistory) {
            history.pushState({ gsSearchTakeover: true }, "", this._mobileTrigger.href);
            this._pushedHistory = true;
        }
    }

    open() {
        if (this._isOpen || !this._header) {
            return;
        }

        this._isOpen = true;
        Menu.closeLocalePanel();
        Menu.closeMegaMenus();
        this._header.classList.add("search-open");
        // Rows inside the panel above the results (summary, filter chips)
        // aren't themselves scrollable, so a wheel/touch scroll there was
        // falling through to the dimmed page underneath rather than doing
        // nothing. overflow: hidden on <body> alone doesn't stop that on iOS
        // Safari — touchmove drags still scroll the page underneath a fixed
        // overlay regardless of body overflow. Freezing the body in place
        // (position: fixed, offset by the current scroll position) is the
        // standard workaround: there's nothing left for iOS to scroll, since
        // the frozen body no longer has any overflow of its own. The panel's
        // own scrollable regions (filter pane, results) are unaffected —
        // they keep their own overflow: auto regardless of what the body does.
        // The takeover is in normal flow with the page content hidden, so
        // there's nothing underneath to freeze.
        if (!this._isTakeover) {
            this._frozenScrollY = window.scrollY;
            document.body.style.top = `-${this._frozenScrollY}px`;
            document.body.classList.add("search-scroll-lock");
        }
        renderRecentlyViewed(this._recentList);
        if (!this._hasSeededLocale && this._panel) {
            seedLocaleFilter(this._panel, this._panel.dataset.seedLocale, () => {
                this._hasSeededLocale = true;
            });
        }
    }

    close() {
        if (!this._isOpen || !this._header) {
            return;
        }

        this._isOpen = false;
        this._header.classList.remove("search-open");
        document.body.classList.remove("search-scroll-lock", "search-takeover");
        document.body.style.top = "";
        this._input?.blur();
        window.scrollTo(0, this._frozenScrollY);

        if (this._isTakeover) {
            this._isTakeover = false;
            // Closed by Cancel/Esc rather than Back: pop the /search/ entry
            // the takeover pushed, so the URL matches the page again. The
            // resulting popstate finds the takeover already closed.
            if (this._pushedHistory) {
                this._pushedHistory = false;
                history.back();
            }
        }
    }

    // Scoped to the search field + panel specifically, not the whole
    // header — the locale switcher and facet-nav live inside #site-header
    // too, so checking against the whole header treated clicking
    // any of them as an "inside" click and never closed the panel.
    handleOutsideClick(target: Node) {
        // The takeover covers the whole page — Cancel, Esc, or Back close it.
        if (!this._isOpen || this._isTakeover) {
            return;
        }

        const insideSearch = (this._searchWrap && this._searchWrap.contains(target)) || (this._panel && this._panel.contains(target));

        if (!insideSearch) {
            this.close();
        }
    }

    private handleKeydown(e: KeyboardEvent) {
        if (e.key === "Escape") {
            if (this._isOpen) {
                this.close();
            }
            return;
        }

        if (e.key !== "/" || this._isOpen) {
            return;
        }

        // Don't hijack "/" while the user is typing into some other field.
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
            return;
        }

        e.preventDefault();
        this._input?.focus();
    }
}

export default new Search();
